import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ChefHat, Sparkles, Plus, Trash2, Save, X, TrendingUp, RefreshCw, Star, Heart, Eye, AlertTriangle, HelpCircle, Zap, Info } from "lucide-react";
import ItemCombobox from "@/pages/staff/procurement/ItemCombobox";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const marginClass = (pct) => {
  if (pct >= 65) return "text-[hsl(var(--forest))] font-semibold";
  if (pct >= 45) return "text-[hsl(var(--brass-2))]";
  return "text-[hsl(6_62%_45%)] font-semibold";
};

const fcClass = (pct) => {
  if (pct <= 0) return "text-[hsl(var(--ink-soft))]";
  if (pct <= 35) return "text-[hsl(var(--forest))] font-semibold";
  if (pct <= 55) return "text-[hsl(var(--brass-2))]";
  return "text-[hsl(6_62%_45%)] font-semibold";
};

// Bucket definitions — used by both the heatmap strip and the row-level chip.
const BUCKETS = {
  star:    { label: "Stars",        icon: Star,         swatch: "bg-[hsl(150_45%_35%)]",   ring: "ring-[hsl(150_45%_35%)]",   copy: "Food cost ≤ 30% — promote these" },
  healthy: { label: "Healthy",      icon: Heart,        swatch: "bg-[hsl(140_35%_50%)]",   ring: "ring-[hsl(140_35%_50%)]",   copy: "30–40% food cost" },
  watch:   { label: "Watch",        icon: Eye,          swatch: "bg-[hsl(40_75%_50%)]",    ring: "ring-[hsl(40_75%_50%)]",    copy: "40–55% — trim or reprice" },
  bleeding:{ label: "Bleeding",     icon: AlertTriangle,swatch: "bg-[hsl(6_65%_48%)]",     ring: "ring-[hsl(6_65%_48%)]",     copy: "> 55% or negative margin" },
  missing: { label: "Missing data", icon: HelpCircle,   swatch: "bg-[hsl(30_10%_60%)]",    ring: "ring-[hsl(30_10%_60%)]",    copy: "No recipe or no menu price yet" },
};

const classifyRow = (r) => {
  if (!r.has_recipe || !(r.price > 0)) return "missing";
  const fc = r.food_cost_pct || 0;
  const marginNeg = r.margin_rs !== undefined ? r.margin_rs <= 0 : false;
  if (fc > 55 || marginNeg) return "bleeding";
  if (fc > 40) return "watch";
  if (fc > 30) return "healthy";
  return "star";
};

export default function Recipes() {
  const [report, setReport] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [editingMenuItem, setEditingMenuItem] = useState(null);
  const [sectionFilter, setSectionFilter] = useState("all");
  const [q, setQ] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [bucketFilter, setBucketFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [rep, inv] = await Promise.all([
        api.get("/staff/recipes/profitability"),
        api.get("/staff/inventory/items"),
      ]);
      setReport(rep.data);
      setInventory(inv.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load recipes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sections = useMemo(() => {
    const set = new Set(report.rows.map((r) => r.section).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [report.rows]);

  const rowsWithBucket = useMemo(
    () => (report.rows || []).map((r) => ({ ...r, bucket: classifyRow(r) })),
    [report.rows],
  );

  const bucketCounts = useMemo(() => {
    const counts = { star: 0, healthy: 0, watch: 0, bleeding: 0, missing: 0 };
    for (const r of rowsWithBucket) counts[r.bucket] = (counts[r.bucket] || 0) + 1;
    return counts;
  }, [rowsWithBucket]);

  const filteredRows = useMemo(() => {
    let rs = rowsWithBucket;
    if (bucketFilter !== "all") rs = rs.filter((r) => r.bucket === bucketFilter);
    if (sectionFilter !== "all") rs = rs.filter((r) => r.section === sectionFilter);
    if (onlyMissing) rs = rs.filter((r) => !r.has_recipe);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      rs = rs.filter((r) => r.menu_item_name.toLowerCase().includes(s));
    }
    return rs;
  }, [rowsWithBucket, sectionFilter, onlyMissing, q, bucketFilter]);

  const summary = report.summary || {};

  return (
    <section data-testid="recipes-page" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="display text-3xl inline-flex items-center gap-2">
            <ChefHat /> Recipe costing
          </h1>
          <p className="text-sm text-[hsl(var(--ink-soft))]">
            Link each menu item to a Bill of Materials of inventory ingredients. Cost per portion is computed from your live inventory rates; food cost % uses the menu price.
          </p>
        </div>
        <button
          data-testid="recipes-refresh"
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 border border-[hsl(var(--rule))] text-sm hover:bg-[hsl(var(--bg-2))]"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </header>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile testid="summary-menu-total" label="Menu items" value={summary.menu_items_total || 0} />
        <SummaryTile testid="summary-with-recipe" label="With a recipe" value={summary.menu_items_with_recipe || 0} accent />
        <SummaryTile
          testid="summary-blended-fc"
          label="Blended food-cost %"
          value={`${(summary.blended_food_cost_pct || 0).toFixed(1)}%`}
          highlight={fcClass(summary.blended_food_cost_pct || 0)}
        />
        <SummaryTile
          testid="summary-avg-margin"
          label="Avg. margin %"
          value={`${(summary.avg_margin_pct || 0).toFixed(1)}%`}
          highlight={marginClass(summary.avg_margin_pct || 0)}
        />
      </div>

      {/* Profitability heatmap */}
      <ProfitabilityHeatmap
        rows={rowsWithBucket}
        counts={bucketCounts}
        active={bucketFilter}
        onSelect={setBucketFilter}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          data-testid="recipes-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search menu items…"
          className="border border-[hsl(var(--rule))] px-3 py-2 text-sm bg-[hsl(var(--bg))] w-56"
        />
        <select
          data-testid="recipes-section-filter"
          value={sectionFilter}
          onChange={(e) => setSectionFilter(e.target.value)}
          className="border border-[hsl(var(--rule))] px-3 py-2 text-sm bg-[hsl(var(--bg))]"
        >
          {sections.map((s) => (
            <option key={s} value={s}>{s === "all" ? "All sections" : s}</option>
          ))}
        </select>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            data-testid="recipes-only-missing"
            type="checkbox"
            checked={onlyMissing}
            onChange={(e) => setOnlyMissing(e.target.checked)}
          />
          Only items without a recipe
        </label>
      </div>

      {/* Table */}
      <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-2))] text-left text-xs uppercase tracking-wide text-[hsl(var(--ink-soft))] sticky top-0 z-10 shadow-[0_1px_0_hsl(var(--rule))]">
            <tr>
              <th className="px-4 py-2">Menu item</th>
              <th className="px-4 py-2">Section</th>
              <th className="px-4 py-2 text-right">Menu price</th>
              <th className="px-4 py-2 text-right">Cost / portion</th>
              <th className="px-4 py-2 text-right">Food cost %</th>
              <th className="px-4 py-2 text-right">Margin</th>
              <th className="px-4 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-[hsl(var(--ink-soft))]">Loading…</td></tr>
            )}
            {!loading && filteredRows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-[hsl(var(--ink-soft))]">No matching items</td></tr>
            )}
            {!loading && filteredRows.map((r) => (
              <tr key={r.menu_item_id} data-testid={`recipe-row-${r.menu_item_id}`} className="border-t border-[hsl(var(--rule))]">
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <span
                      title={BUCKETS[r.bucket].label}
                      className={`inline-block w-2 h-2 rounded-full ${BUCKETS[r.bucket].swatch}`}
                    />
                    <span className="font-semibold">{r.menu_item_name}</span>
                  </div>
                  {r.has_recipe && (
                    <div className="text-[11px] text-[hsl(var(--ink-soft))]">
                      {r.ingredients_count} ingredient{r.ingredients_count === 1 ? "" : "s"} · yield {r.yield_portions}
                      {r.ai_generated && <span className="ml-2 text-[hsl(var(--brass-2))]">AI</span>}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2">{r.section}</td>
                <td className="px-4 py-2 text-right">{r.price > 0 ? inr(r.price) : <span className="text-[hsl(var(--ink-soft))]">—</span>}</td>
                <td className="px-4 py-2 text-right">{r.cost_per_portion > 0 ? inr(r.cost_per_portion) : <span className="text-[hsl(var(--ink-soft))]">—</span>}</td>
                <td className={`px-4 py-2 text-right ${fcClass(r.food_cost_pct)}`}>{r.food_cost_pct > 0 ? `${r.food_cost_pct.toFixed(1)}%` : "—"}</td>
                <td className={`px-4 py-2 text-right ${r.has_recipe && r.price > 0 ? marginClass(r.margin_pct) : "text-[hsl(var(--ink-soft))]"}`}>
                  {r.has_recipe && r.price > 0 ? `${inr(r.margin_rs)} · ${r.margin_pct.toFixed(1)}%` : "—"}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    data-testid={`recipe-edit-${r.menu_item_id}`}
                    onClick={() => setEditingMenuItem(r)}
                    className="text-[hsl(var(--forest))] hover:underline"
                  >
                    {r.has_recipe ? "Edit" : "Add recipe"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingMenuItem && (
        <RecipeEditor
          menuItem={editingMenuItem}
          inventory={inventory}
          onClose={() => setEditingMenuItem(null)}
          onSaved={() => { setEditingMenuItem(null); load(); }}
        />
      )}
    </section>
  );
}

function ProfitabilityHeatmap({ rows, counts, active, onSelect }) {
  const total = rows.length || 1;
  const bucketOrder = ["star", "healthy", "watch", "bleeding", "missing"];

  // Group items by section so the strip reads left-to-right by menu structure.
  const grouped = useMemo(() => {
    const bySection = new Map();
    for (const r of rows) {
      const key = r.section || "Other";
      if (!bySection.has(key)) bySection.set(key, []);
      bySection.get(key).push(r);
    }
    // Sort so bleeding/watch bubble to the front WITHIN each section — draws the eye there.
    const bucketPriority = { bleeding: 0, watch: 1, missing: 2, healthy: 3, star: 4 };
    for (const arr of bySection.values()) {
      arr.sort((a, b) => (bucketPriority[a.bucket] - bucketPriority[b.bucket]) || a.menu_item_name.localeCompare(b.menu_item_name));
    }
    return Array.from(bySection.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  return (
    <section data-testid="profitability-heatmap" className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="eyebrow text-[10px]">Menu profitability heatmap</div>
          <div className="text-xs text-[hsl(var(--ink-soft))]">Click a bucket to filter the table below. Squares are grouped by menu section; hover for details.</div>
        </div>
        <button
          data-testid="heatmap-clear"
          onClick={() => onSelect("all")}
          className={`text-xs px-3 py-1 rounded-full transition-colors ${active === "all" ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "border border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--bg-2))]"}`}
        >
          Show all
        </button>
      </div>

      {/* Bucket tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {bucketOrder.map((k) => {
          const b = BUCKETS[k];
          const count = counts[k] || 0;
          const pct = Math.round((count / total) * 100);
          const isActive = active === k;
          const Icon = b.icon;
          return (
            <button
              key={k}
              data-testid={`heatmap-bucket-${k}`}
              onClick={() => onSelect(isActive ? "all" : k)}
              className={`text-left p-3 border transition-all ${isActive ? `${b.ring} ring-2 border-transparent` : "border-[hsl(var(--rule))] hover:border-[hsl(var(--forest))]"} bg-[hsl(var(--bg))]`}
            >
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${b.swatch} text-white`}>
                  <Icon size={12} />
                </span>
                <span className="text-xs uppercase tracking-wide font-semibold">{b.label}</span>
              </div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="display text-2xl">{count}</span>
                <span className="text-[10px] text-[hsl(var(--ink-soft))]">/ {total} · {pct}%</span>
              </div>
              <div className="mt-1 text-[10px] text-[hsl(var(--ink-soft))] leading-snug">{b.copy}</div>
            </button>
          );
        })}
      </div>

      {/* Section-grouped grid of item squares */}
      <div data-testid="heatmap-grid" className="space-y-2">
        {grouped.map(([section, items]) => (
          <div key={section} className="flex flex-wrap items-center gap-1.5">
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))] w-full sm:w-40 shrink-0">
              {section} <span className="text-[hsl(var(--rule))]">·</span> {items.length}
            </div>
            <div className="flex flex-wrap gap-1 flex-1">
              {items.map((r) => {
                const b = BUCKETS[r.bucket];
                const price = r.price > 0 ? `Rs.${r.price.toFixed(0)}` : "no price";
                const fc = r.has_recipe && r.price > 0 ? `${r.food_cost_pct.toFixed(0)}% fc` : (r.has_recipe ? "no price" : "no recipe");
                const label = `${r.menu_item_name} - ${price} - ${fc}`;
                const dim = active !== "all" && active !== r.bucket;
                return (
                  <span
                    key={r.menu_item_id}
                    data-testid={`heatmap-cell-${r.menu_item_id}`}
                    title={label}
                    className={`inline-block w-3.5 h-3.5 rounded-sm ${b.swatch} transition-opacity ${dim ? "opacity-20" : "opacity-100 hover:opacity-70"}`}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}



function SummaryTile({ label, value, testid, accent, highlight }) {
  return (
    <div data-testid={testid} className={`border ${accent ? "border-[hsl(var(--forest))]" : "border-[hsl(var(--rule))]"} bg-[hsl(var(--bg))] p-4`}>
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{label}</div>
      <div className={`display text-2xl mt-1 ${highlight || ""}`}>{value}</div>
    </div>
  );
}

function RecipeEditor({ menuItem, inventory, onClose, onSaved }) {
  const [yieldPortions, setYieldPortions] = useState(1);
  const [ingredients, setIngredients] = useState([]);
  const [notes, setNotes] = useState("");
  const [aiGenerated, setAiGenerated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPortions, setAiPortions] = useState(4);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // 404 is expected when no recipe exists yet — use validateStatus to keep the console clean.
        const res = await api.get(`/staff/recipes/menu-item/${menuItem.menu_item_id}`, {
          validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
        });
        if (res.status === 404) {
          // Fresh form — nothing to prefill
        } else {
          setYieldPortions(res.data.yield_portions || 1);
          setIngredients((res.data.ingredients || []).map((i) => ({ ...i })));
          setNotes(res.data.notes || "");
          setAiGenerated(!!res.data.ai_generated);
        }
      } catch (err) {
        toast.error("Failed to load recipe");
      } finally {
        setLoading(false);
      }
    })();
  }, [menuItem.menu_item_id]);

  const totalCost = ingredients.reduce((acc, i) => acc + Number(i.qty || 0) * Number(i.rate || 0), 0);
  const costPerPortion = yieldPortions > 0 ? totalCost / yieldPortions : 0;
  const foodCostPct = menuItem.price > 0 && costPerPortion > 0 ? (costPerPortion / menuItem.price) * 100 : 0;

  const addLine = () => {
    setIngredients((prev) => [...prev, { item_id: "", item_name: "", qty: 0, unit: "g", rate: 0 }]);
  };

  const removeLine = (idx) => setIngredients((prev) => prev.filter((_, i) => i !== idx));

  const updateLine = (idx, patch) => {
    setIngredients((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const runAiSuggest = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/staff/recipes/ai-suggest", {
        menu_item_id: menuItem.menu_item_id,
        portions: aiPortions,
      });
      setYieldPortions(res.data.yield_portions || aiPortions);
      setIngredients(res.data.ingredients || []);
      setNotes(res.data.notes || "");
      setAiGenerated(true);
      const unmatched = res.data.unmatched_count || 0;
      if (unmatched > 0) {
        toast.warning(`AI suggested ${res.data.ingredients.length} ingredients — ${unmatched} are not in your catalog. Set rates manually.`);
      } else {
        toast.success(`AI suggested ${res.data.ingredients.length} ingredients`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI suggestion failed");
    } finally {
      setAiLoading(false);
    }
  };

  const save = async () => {
    const validIngs = ingredients
      .map((i) => ({
        item_id: i.item_id || "",
        item_name: (i.item_name || "").trim(),
        qty: Number(i.qty || 0),
        unit: i.unit || "g",
        rate: Number(i.rate || 0),
      }))
      .filter((i) => i.item_name);
    if (validIngs.length === 0) {
      toast.error("Add at least one ingredient");
      return;
    }
    setSaving(true);
    try {
      await api.post("/staff/recipes", {
        menu_item_id: menuItem.menu_item_id,
        yield_portions: Number(yieldPortions) || 1,
        ingredients: validIngs,
        notes,
        ai_generated: aiGenerated,
      });
      toast.success("Recipe saved");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-[hsl(var(--ink))]/60 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="recipe-editor">
      <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-4xl my-6">
        <div className="p-4 border-b border-[hsl(var(--rule))] flex items-center justify-between">
          <div>
            <div className="display text-xl">{menuItem.menu_item_name}</div>
            <div className="text-xs text-[hsl(var(--ink-soft))]">{menuItem.section} · Menu price: {menuItem.price_raw || "—"}</div>
          </div>
          <button data-testid="recipe-editor-close" onClick={onClose} className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-[hsl(var(--ink-soft))]">Loading…</div>
        ) : (
          <div className="p-4 space-y-4">
            {/* AI suggest bar */}
            <div className="border border-[hsl(var(--brass))]/40 bg-[hsl(var(--brass))]/5 p-3 flex flex-wrap items-center gap-3">
              <Sparkles size={16} className="text-[hsl(var(--brass-2))]" />
              <span className="text-sm">AI suggest a starter recipe for</span>
              <input
                data-testid="recipe-ai-portions"
                type="number"
                min="1"
                value={aiPortions}
                onChange={(e) => setAiPortions(Number(e.target.value) || 1)}
                className="w-16 border border-[hsl(var(--rule))] px-2 py-1 text-sm bg-[hsl(var(--bg))]"
              />
              <span className="text-sm">portion(s)</span>
              <button
                data-testid="recipe-ai-run"
                onClick={runAiSuggest}
                disabled={aiLoading}
                className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 bg-[hsl(var(--brass-2))] text-white text-sm hover:opacity-90 disabled:opacity-50"
              >
                <Sparkles size={14} /> {aiLoading ? "Thinking…" : "Suggest ingredients"}
              </button>
            </div>

            {/* Yield + Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-wide text-[hsl(var(--ink-soft))]">Yield (portions)</span>
                <input
                  data-testid="recipe-yield"
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={yieldPortions}
                  onChange={(e) => setYieldPortions(Number(e.target.value) || 1)}
                  className="mt-1 block w-full border border-[hsl(var(--rule))] px-2 py-1.5 text-sm bg-[hsl(var(--bg))]"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-wide text-[hsl(var(--ink-soft))]">Notes (optional)</span>
                <input
                  data-testid="recipe-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1 block w-full border border-[hsl(var(--rule))] px-2 py-1.5 text-sm bg-[hsl(var(--bg))]"
                />
              </label>
            </div>

            {/* Ingredients */}
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className="text-xs uppercase tracking-wide text-[hsl(var(--ink-soft))]">Ingredients</div>
                  <span title="Ingredients don't need to be in the inventory catalog — type any name, unit and rate. When a name matches an inventory item, the ⚡ button pulls the current rate." className="text-[hsl(var(--ink-soft))] cursor-help">
                    <Info size={12} />
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {(() => {
                    const stale = ingredients.filter((ing) => {
                      const m = inventory.find((x) => (x.name || "").trim().toLowerCase() === (ing.item_name || "").trim().toLowerCase());
                      return m && Number(m.last_rate || 0) > 0 && Math.abs(Number(m.last_rate) - Number(ing.rate || 0)) > 0.01;
                    }).length;
                    if (stale === 0) return null;
                    return (
                      <button
                        type="button"
                        data-testid="recipe-refresh-all-rates"
                        onClick={() => {
                          setIngredients((prev) => prev.map((ing) => {
                            const m = inventory.find((x) => (x.name || "").trim().toLowerCase() === (ing.item_name || "").trim().toLowerCase());
                            if (m && Number(m.last_rate || 0) > 0) {
                              return { ...ing, item_id: m.id, unit: m.unit || ing.unit, rate: Number(m.last_rate) };
                            }
                            return ing;
                          }));
                          toast.success(`Refreshed ${stale} rate${stale === 1 ? "" : "s"} from inventory`);
                        }}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors"
                      >
                        <Zap size={11} /> Pull {stale} stale rate{stale === 1 ? "" : "s"} from catalog
                      </button>
                    );
                  })()}
                  <button data-testid="recipe-add-line" onClick={addLine} className="inline-flex items-center gap-1 text-xs text-[hsl(var(--forest))] hover:underline">
                    <Plus size={12} /> Add ingredient
                  </button>
                </div>
              </div>
              <div className="border border-[hsl(var(--rule))] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[hsl(var(--bg-2))] text-left text-[11px] uppercase tracking-wide text-[hsl(var(--ink-soft))] sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-1.5 w-[36%]">Ingredient</th>
                      <th className="px-2 py-1.5 w-16 text-right">Qty</th>
                      <th className="px-2 py-1.5 w-20">Unit</th>
                      <th className="px-2 py-1.5 w-28 text-right">Rate ₹</th>
                      <th className="px-2 py-1.5 w-24 text-right">Line ₹</th>
                      <th className="px-2 py-1.5 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ingredients.length === 0 && (
                      <tr><td colSpan={6} className="px-3 py-4 text-center text-[hsl(var(--ink-soft))] text-xs">No ingredients yet — click <span className="font-semibold">Add ingredient</span> or use AI suggest.</td></tr>
                    )}
                    {ingredients.map((ing, idx) => {
                      const catalogMatch = inventory.find(
                        (x) => (x.name || "").trim().toLowerCase() === (ing.item_name || "").trim().toLowerCase(),
                      );
                      const catalogRate = catalogMatch ? Number(catalogMatch.last_rate || 0) : 0;
                      const rateDiffers = catalogMatch && catalogRate > 0 && Math.abs(catalogRate - Number(ing.rate || 0)) > 0.01;
                      return (
                      <tr key={idx} data-testid={`recipe-line-${idx}`} className="border-t border-[hsl(var(--rule))] align-top">
                        <td className="px-2 py-1.5">
                          <ItemCombobox
                            items={inventory}
                            value={{ item_id: ing.item_id, item_name: ing.item_name }}
                            onPick={(it) => updateLine(idx, {
                              item_id: it.id,
                              item_name: it.name,
                              unit: it.unit || "kg",
                              rate: Number(it.last_rate || 0),
                            })}
                            onFreeText={(txt) => updateLine(idx, { item_id: "", item_name: txt })}
                            inputTestId={`recipe-line-item-${idx}`}
                          />
                          {!catalogMatch && (ing.item_name || "").trim() && (
                            <div className="text-[10px] text-[hsl(var(--ink-soft))] mt-1 italic">
                              Free-text · not in catalog
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <input
                            data-testid={`recipe-line-qty-${idx}`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={ing.qty}
                            onChange={(e) => updateLine(idx, { qty: Number(e.target.value) || 0 })}
                            className="w-full border border-[hsl(var(--rule))] px-1 py-1 text-xs text-right bg-[hsl(var(--bg))]"
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <select
                            data-testid={`recipe-line-unit-${idx}`}
                            value={ing.unit}
                            onChange={(e) => updateLine(idx, { unit: e.target.value })}
                            className="w-full border border-[hsl(var(--rule))] px-1 py-1 text-xs bg-[hsl(var(--bg))]"
                          >
                            {["kg", "g", "litre", "ml", "pcs", "bunch", "bottle", "packet"].map((u) => (
                              <option key={u} value={u}>{u}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <div className="flex items-stretch gap-1">
                            <input
                              data-testid={`recipe-line-rate-${idx}`}
                              type="number"
                              min="0"
                              step="0.01"
                              value={ing.rate}
                              onChange={(e) => updateLine(idx, { rate: Number(e.target.value) || 0 })}
                              className="flex-1 border border-[hsl(var(--rule))] px-1 py-1 text-xs text-right bg-[hsl(var(--bg))]"
                            />
                            {catalogMatch && catalogRate > 0 && (
                              <button
                                type="button"
                                data-testid={`recipe-line-pull-rate-${idx}`}
                                onClick={() => updateLine(idx, {
                                  item_id: catalogMatch.id,
                                  unit: catalogMatch.unit || ing.unit,
                                  rate: catalogRate,
                                })}
                                title={`Pull ₹${catalogRate}/${catalogMatch.unit} from inventory (current rate)`}
                                className={`px-1.5 border rounded ${rateDiffers ? "border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] animate-pulse" : "border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--forest))]"}`}
                              >
                                <Zap size={11} />
                              </button>
                            )}
                          </div>
                          {rateDiffers && (
                            <div className="text-[10px] text-[hsl(var(--brass-2))] mt-0.5">
                              Catalog: ₹{catalogRate}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right text-xs">
                          {inr(Number(ing.qty || 0) * Number(ing.rate || 0))}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <button
                            data-testid={`recipe-line-remove-${idx}`}
                            onClick={() => removeLine(idx)}
                            className="text-[hsl(6_62%_45%)] hover:opacity-80"
                          >
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Live totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryTile testid="editor-total-cost" label="Total ingredient cost" value={inr(totalCost)} />
              <SummaryTile testid="editor-cost-per-portion" label="Cost per portion" value={inr(costPerPortion)} accent />
              <SummaryTile
                testid="editor-food-cost"
                label="Food cost %"
                value={foodCostPct > 0 ? `${foodCostPct.toFixed(1)}%` : "—"}
                highlight={fcClass(foodCostPct)}
              />
              <SummaryTile
                testid="editor-margin"
                label="Margin"
                value={menuItem.price > 0 ? `${inr(menuItem.price - costPerPortion)}` : "—"}
                highlight={marginClass(menuItem.price > 0 ? ((menuItem.price - costPerPortion) / menuItem.price) * 100 : 0)}
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2">
              <button
                data-testid="recipe-cancel"
                onClick={onClose}
                className="px-3 py-2 border border-[hsl(var(--rule))] text-sm hover:bg-[hsl(var(--bg-2))]"
              >
                Cancel
              </button>
              <button
                data-testid="recipe-save"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 px-3 py-2 bg-[hsl(var(--forest))] text-[hsl(var(--bg))] text-sm hover:bg-[hsl(var(--forest-2))] disabled:opacity-50"
              >
                <Save size={14} /> {saving ? "Saving…" : "Save recipe"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
